/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* =========================
   Allora related product popup
   Native Shopify popup for required companion items
   ========================= */

(function () {
  /*
    -----------------------------------------
    HOW TO ADD NEW MATCHES LATER
    -----------------------------------------

    1. Add a new related group in RELATED_GROUPS

    Example:
    al7_08_valve: {
      variantId: 52057876496687,
      productHandle: 'pressure-balancing-rough-in-valve-with-diverter-and-stops-al7-08',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/AL7-08_Popup.jpg?v=1776277827',
      title: 'Complete your shower system',
      body: 'This trim kit requires the compatible AL7-08 mixing valve to complete installation.',
      buttonText: 'Add Matching Valve'
    }

    2. Map any trigger product handles to that group in PRODUCT_TO_GROUP

    Example:
    'matte-black-single-handle-tub-and-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7008-sh-bl': 'al7_08_valve'

    That is all you should normally need to change.
  */

  const RELATED_GROUPS = {
    al7_08_valve: {
      variantId: 52057876496687,
      productHandle: 'pressure-balancing-rough-in-valve-with-diverter-and-stops-al7-08',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/AL7-08_Popup.jpg?v=1776277827',
      title: 'Complete your shower system',
      body: 'This trim kit requires the compatible AL7-08 mixing valve to complete installation.',
      buttonText: 'Add Matching Valve'
    },

    grhm_3018: {
      variantId: 50639788212527,
      productHandle: 'grhm-3018-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GRHM-3018.jpg?v=1755020768',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    grhm_2318: {
      variantId: 50639781953839,
      productHandle: 'grhm-2318-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GRHM-2318.png?v=1755020769',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    grhm_3318: {
      variantId: 50639795781935,
      productHandle: 'grhm-3318-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GRHM-3318.webp?v=1747062460',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_2318: {
      variantId: 50639676473647,
      productHandle: 'gr-2318-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GRID-KSN-2318-Sink-Grid.jpg?v=1755020775',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_2718: {
      variantId: 50639715270959,
      productHandle: 'gr-2718-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GR-2718_PRODUCT_PHOTO.png?v=1755020773',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_3218: {
      variantId: 50639745253679,
      productHandle: 'gr-3218-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GR-3218.jpg?v=1747061898',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_3118: {
      variantId: 50639722086703,
      productHandle: 'gr-3118-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GR-3118-kitchen-sink-grid-set_c804d678-675f-4127-8873-e75bc0c9cd68.webp?v=1755020773',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_1518: {
      variantId: 50639633252655,
      productHandle: 'gr-1518-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GR-1518.jpg?v=1755020778',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_1917: {
      variantId: 50639661433135,
      productHandle: 'gr-1917-r25-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GR-1917-R25-Sink-Grid.jpg?v=1755020777',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_2117: {
      variantId: 50639672115503,
      productHandle: 'gr-2117-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GR-2117-R25-Sink-Grid.jpg?v=1755020776',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GR_3018_R20: {
      variantId: 50639462072623,
      productHandle: 'g-3018-kitchen-sink-grid',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GRHM-3018.jpg?v=1755020768',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    G_3118: {
      variantId: 51377605706031,
      productHandle: '12-3-4-stainless-steel-kitchen-sink-bottom-grid-g-3118-d-r20',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/G-3118-S-R20-kitchen-sink-grid.webp?v=1755020759',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    G_2318: {
      variantId: 51377446420783,
      productHandle: '19-3-4-stainless-steel-grid-bottom-protector-for-kitchen-sink-g-2318-s-r20',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/G-2318-S-R20-kitchen-sink-grid.webp?v=1755020762',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    G_2718: {
      variantId: 51377594597679,
      productHandle: '23-3-4-stainless-steel-kitchen-sink-bottom-grid-g-2718-s-r20',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/G-2718-S-R20-kitchen-sink-grid.webp?v=1755020761',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    G_3118_S: {
      variantId: 51377861656879,
      productHandle: '12-3-4-stainless-steel-kitchen-sink-bottom-grid-g-3118-s-r20',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/G-3118-S-R20-kitchen-sink-grid.webp?v=1755020759',
      title: 'Add the matching sink grid',
      body: 'Help protect your sink surface and improve drainage with the matching bottom grid.',
      buttonText: 'Add Matching Grid',
      discountRate: 20
    },
    GF_330_BL: {
      variantId: 50605842628911,
      productHandle: 'gf-330-bl',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GF-330-BL.webp?v=1755020788',
      title: 'Add the matching garbage disposal flange',
      body: 'Most garbage disposals include a standard flange. Upgrade to a matching finish for a more complete look.',
      buttonText: 'Add Matching Flange',
      discountRate: 20
    },
    GF_330_GL: {
      variantId: 50605898596655,
      productHandle: 'gf-330-g-matte-black-garbage-disposal-flange',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/GF-330-GL.webp?v=1755020787',
      title: 'Add the matching garbage disposal flange',
      body: 'Most garbage disposals include a standard flange. Upgrade to a matching finish for a more complete look.',
      buttonText: 'Add Matching Flange',
      discountRate: 20
    },
    AL5_08_BN: {
      variantId: 58283496276271,
      productHandle: 'al5-08-bn-stainless-steel-pvc-solvent-weld-shower-drain',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/AL5-08-BN.webp?v=1785783548',
      title: 'Add the matching shower drain',
      body: 'Get a matching shower drain in brushed nickel, chrome or matte black.',
      buttonText: 'Add Matching Flange',
      discountRate: 20
    },
    al7_01_valve: {
      variantId: 49845976596783,
      productHandle: 'rough-in-valve',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0691/8878/0335/files/AL7-01_diverter-valve-800x533.webp?v=1744214461',
      title: 'Complete your shower system',
      body: 'This trim kit requires the compatible AL7-01 mixing valve to complete installation.',
      buttonText: 'Add Matching Valve'
    }
  };

  const PRODUCT_TO_GROUP = {
    '32-x-32-square-acrylic-shower-base-sp-3232-10': 'AL5_08_BN',
    '36-x-36-square-acrylic-shower-base-sp-3636-10': 'AL5_08_BN',
    '48-x-48-square-acrylic-shower-base-sp-4848-10': 'AL5_08_BN',
    '60-x-30-rectangular-acrylic-shower-base-sp-6030-10': 'AL5_08_BN',
    '60-x-32-rectangular-acrylic-shower-base-sp-6032-10': 'AL5_08_BN',
    '60-x-36-large-rectangular-acrylic-shower-base-sp-6036-10': 'AL5_08_BN',
    '60-x-32-roll-in-ada-acrylic-shower-base-ada-sp-6032-10': 'AL5_08_BN',
    '62-x-32-inch-roll-in-ada-acrylic-shower-base-ada-sp-6232-10': 'AL5_08_BN',
    '62-x-38-inch-large-barrier-free-roll-in-ada-acrylic-shower-base-ada-sp-6238-10': 'AL5_08_BN',
    'matte-black-single-handle-tub-and-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7008-sh-bl': 'al7_08_valve',
    'brushed-nickel-single-handle-tub-and-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7008-sh-bn': 'al7_08_valve',
    'chrome-single-handle-tub-and-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7002-sh-c-copy': 'al7_08_valve',
    'chrome-single-handle-tub-and-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7008-sh-c-copy': 'al7_08_valve',
    'brushed-nickel-single-handle-tub-and-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7008-sh-bn-copy': 'al7_08_valve',
    'matte-black-single-handle-shower-trim-kit-with-integrated-diverter-and-hand-shower-st-7018-sh-bl': 'al7_08_valve',
    'st-7001-sh-bn-bath-shower-trim-kit': 'al7_01_valve',
    'st-7001-sh-c-bath-shower-trim-kit': 'al7_01_valve',
    'st-7001-sh-bl-bath-shower-trim-kit': 'al7_01_valve',
    'st-7011-sh-bl-bath-shower-trim-kit': 'al7_01_valve',
    'st-7011-sh-bn-bath-shower-trim-kit': 'al7_01_valve',
    'st-7011-sh-c-bath-shower-trim-kit': 'al7_01_valve',
    'st-7002-sh-c-bath-shower-trim-kit-with-head': 'al7_01_valve',
    'st-7002-sh-bn-bath-shower-trim-kit-with-head': 'al7_01_valve',
    'st-7002-sh-bl-bath-shower-trim-kit-with-head': 'al7_01_valve',
    'st-7012-sh-bl-bath-shower-trim-kit-with-head': 'al7_01_valve',
    'st-7012-sh-bn-bath-shower-trim-kit-with-head': 'al7_01_valve',
    'st-7012-sh-c-bath-shower-trim-kit-with-head': 'al7_01_valve',
    'st-7004-sh-c-bath-shower-trim-kit': 'al7_01_valve',
    'st-7004-sh-bn-bath-shower-trim-kit': 'al7_01_valve',
    'st-7004-sh-bl-bath-shower-trim-kit': 'al7_01_valve',
    'st-7014-sh-bl-bath-shower-trim-kit': 'al7_01_valve',
    'st-7014-sh-bn-bath-shower-trim-kit': 'al7_01_valve',
    'st-7014-sh-c-bath-shower-trim-kit': 'al7_01_valve',
    'kh-3018-10-s-r0-handmade-stainless-steel-kitchen-sink': 'grhm_3018',
    'kh-3018-7-s-r15-handmade-stainless-steel-kitchen-sink': 'grhm_3018',
    'ada-kh-3018-s-r15-handmade-single-bowl-kitchen-sink': 'grhm_3018',
    'kh-2318-10-s-r15-handmade-stainless-steel-kitchen-sink': 'grhm_2318',
    'kh-2318-9-s-r20-handmade-stainless-steel-kitchen-sink': 'grhm_2318',
    'kh-2318-7-s-r15-handmade-stainless-steel-kitchen-sink': 'grhm_2318',
    'ada-kh-2318-s-r20-handmade-stainless-steel-kitchen-sink': 'grhm_2318',
    'kh-3318-9-d-r0-handmade-stainless-steel-kitchen-sink': 'grhm_3318',
    'kh-3218-7-d-r15-handmade-double-bowl-kitchen-sink': 'grhm_3318',
    'ksn-2318-9-s-single-bowl-stainless-steel-undermount-sink': 'GR_2318',
    'ksn-2318-7-s-single-bowl-undermount-sink': 'GR_2318',
    'ada-ksn-2318-s-single-bowl-undermount-sink': 'GR_2318',
    'ksn-3218-9-d-undermount-double-bowl-kitchen-sink': 'GR_3218',
    'ksn-3118-9-d-undermount-double-bowl-kitchen-sink': 'GR_3118',
    'ada-ksn-3118-d-double-bowl-undermount-sink': 'GR_3118',
    'ksn-1518-8-s-single-bowl-undermount-bar-sink': 'GR_1518',
    'ksn-1917-9-s-r25-single-bowl-undermount-sink': 'GR_1917',
    'ksn-2117-r25-single-bowl-undermount-sink': 'GR_2117',
    'kh-3018-9-s-r20-handmade-stainless-steel-kitchen-sink': 'GR_3018_R20',
    'ada-kh-3118-d-r15-handmade-stainless-steel-kitchen-sink-1': 'G_3118',
    'ada-kh-3118-d-r15-handmade-stainless-steel-kitchen-sink': 'G_3118',
    'kh-3118-9-d-r20-handmade-single-bowl-kitchen-sink': 'G_3118',
    'kh-2318-9-s-r20-handmade-stainless-steel-kitchen-sink': 'G_2318',
    'ada-kh-2318-s-r20-handmade-stainless-steel-kitchen-sink': 'G_2318',
    'ada-kh-2718-s-r20-handmade-single-bowl-kitchen-sink': 'G_2718',
    'kh-2718-9-s-r20-handmade-stainless-steel-kitchen-sink': 'G_2718',
    'ada-kh-3118-s-r20-handmade-stainless-steel-kitchen-sink': 'G_3118_S',
    'kh-3118-9-s-r20': 'G_3118_S',
    'gc-3118-10-s-black-granite-composite-kitchen-sink': 'GF_330_BL',
    'gc-3118-10-d-black-granite-composite-kitchen-sink': 'GF_330_BL',
    'ksn-3018-9-s-gm-gunmetal-stainless-steel-kitchen-sink': 'GF_330_BL',
    'kh-3018-10-s-r15-gm-gunmetal-black-matte-stainless-steel-handmade-sink': 'GF_330_BL',
    'kh-2718-10-s-r15-gm-gunmetal-black-matte-stainless-steel-handmade-sink': 'GF_330_BL',
    'kh-3018-9-s-r15-gl-gold-stainless-steel-kitchen-sink': 'GF_330_GL',
  };

  const POPUP_ID = 'allora-related-product-popup';
  const STYLE_ID = 'allora-related-product-popup-styles';

  function getCurrentProductHandle() {
    const path = window.location.pathname || '';
    const match = path.match(/^\/products\/([^/?#]+)/i);
    return match ? match[1] : null;
  }

  function getCurrentGroupKey() {
    const handle = getCurrentProductHandle();
    if (!handle) return null;
    return PRODUCT_TO_GROUP[handle] || null;
  }

  function getCurrentConfig() {
    const groupKey = getCurrentGroupKey();
    if (!groupKey) return null;
    return RELATED_GROUPS[groupKey] || null;
  }

  function getDiscountMessage(config) {
    if (!config || config.discountRate === undefined || config.discountRate === null || config.discountRate === '') {
      return '';
    }
    const rate = Number(config.discountRate);
    if (!Number.isFinite(rate) || rate <= 0) {
      return '';
    }
    return `Save ${rate}% at checkout with qualifying purchase.`;
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .allora-popup-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        z-index: 9999;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }

      .allora-popup-overlay.is-open {
        display: flex;
      }

      .allora-popup {
        width: 100%;
        max-width: 720px;
        background: #ffffff;
        border-radius: 4px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
        position: relative;
        overflow: hidden;
      }

      .allora-popup-inner {
        display: grid;
        grid-template-columns: 260px 1fr;
        gap: 0;
        align-items: center;
      }

      .allora-popup-image-wrap {
        background: #ffffff;
        padding: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .allora-popup-image {
        max-width: 100%;
        max-height: 300px;
        object-fit: contain;
        display: block;
      }

      .allora-popup-content {
        padding: 28px 28px 28px 12px;
      }

      .allora-popup-title {
        font-size: 22px;
        line-height: 1.15;
        font-weight: 700;
        color: #111111;
        margin: 0 0 14px;
      }

      .allora-popup-body {
        font-size: 16px;
        line-height: 1.45;
        color: #222222;
        margin: 0 0 18px;
      }

      .allora-popup-actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
      }

      .allora-popup-btn {
        appearance: none;
        border: 0;
        background: #2f96db;
        color: #ffffff;
        font-size: 16px;
        font-weight: 600;
        line-height: 1;
        padding: 16px 24px;
        border-radius: 4px;
        cursor: pointer;
        min-width: 260px;
        transition: opacity 0.2s ease;
      }

      .allora-popup-btn:hover {
        opacity: 0.92;
      }

      .allora-popup-btn[disabled] {
        cursor: default;
        opacity: 0.7;
      }

      .allora-popup-link {
        background: none;
        border: 0;
        padding: 0;
        color: #555555;
        text-decoration: underline;
        font-size: 14px;
        cursor: pointer;
      }

      .allora-popup-close {
        appearance: none;
        border: 0;
        background: none;
        position: absolute;
        top: 12px;
        right: 14px;
        width: 34px;
        height: 34px;
        cursor: pointer;
        font-size: 28px;
        line-height: 1;
        color: #6f8aa5;
      }

      .allora-popup-discount {
        font-size: 14px;
        line-height: 1.4;
        color: #0f766e;
        font-weight: 600;
        margin: -4px 0 18px;
      }

      @media (max-width: 749px) {
        .allora-popup-inner {
          grid-template-columns: 1fr;
        }

        .allora-popup-image-wrap {
          padding: 22px 22px 0;
        }

        .allora-popup-content {
          padding: 20px 22px 24px;
        }

        .allora-popup-btn {
          width: 100%;
          min-width: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function ensurePopupMarkup() {
    if (document.getElementById(POPUP_ID)) return;

    const overlay = document.createElement('div');
    overlay.id = POPUP_ID;
    overlay.className = 'allora-popup-overlay';
    overlay.innerHTML = `
      <div class="allora-popup" role="dialog" aria-modal="true" aria-labelledby="allora-popup-title">
        <button class="allora-popup-close" type="button" aria-label="Close popup">&times;</button>
        <div class="allora-popup-inner">
          <div class="allora-popup-image-wrap">
            <img class="allora-popup-image" alt="" />
          </div>
          <div class="allora-popup-content">
            <h2 id="allora-popup-title" class="allora-popup-title"></h2>
            <p class="allora-popup-body"></p>
            <p class="allora-popup-discount" hidden></p>
            <div class="allora-popup-actions">
              <button class="allora-popup-btn" type="button"></button>
              <button class="allora-popup-link" type="button">I already have it</button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (event) {
      if (
        event.target === overlay ||
        event.target.classList.contains('allora-popup-close') ||
        event.target.classList.contains('allora-popup-link')
      ) {
        closePopup();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closePopup();
      }
    });
  }

  function openPopup(config) {
    ensureStyles();
    ensurePopupMarkup();

    const overlay = document.getElementById(POPUP_ID);
    if (!overlay) return;

    const image = overlay.querySelector('.allora-popup-image');
    const title = overlay.querySelector('.allora-popup-title');
    const body = overlay.querySelector('.allora-popup-body');
    const addBtn = overlay.querySelector('.allora-popup-btn');

    image.src = config.imageUrl;
    image.alt = config.title;
    title.textContent = config.title;
    body.textContent = config.body;

    const discountLine = overlay.querySelector('.allora-popup-discount');
    const discountMessage = getDiscountMessage(config);

    if (discountMessage) {
      discountLine.textContent = discountMessage;
      discountLine.hidden = false;
    } else {
      discountLine.textContent = '';
      discountLine.hidden = true;
    }

    addBtn.textContent = config.buttonText;
    addBtn.disabled = false;
    addBtn.dataset.variantId = String(config.variantId);
    addBtn.dataset.productHandle = config.productHandle || '';
    addBtn.dataset.defaultText = config.buttonText;

    addBtn.onclick = async function () {
      const variantId = Number(addBtn.dataset.variantId);
      const fallbackHandle = addBtn.dataset.productHandle;
      const defaultText = addBtn.dataset.defaultText || 'Add Matching Valve';

      if (!variantId) return;

      try {
        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }]
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add related product');
        }

        await response.json();

        addBtn.textContent = 'Added';

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));

        const miniCart = document.getElementById('mini-cart');
        if (miniCart) {
          miniCart.open = true;
        }

        setTimeout(function () {
          closePopup();
        }, 700);

      } catch (error) {
        console.error('Related product popup add failed:', error);

        if (fallbackHandle) {
          window.location.href = '/products/' + fallbackHandle;
          return;
        }

        addBtn.disabled = false;
        addBtn.textContent = defaultText;
      }
    };

    overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  function closePopup() {
    const overlay = document.getElementById(POPUP_ID);
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.style.overflow = '';
  }

  document.addEventListener('variant:added', function () {
    const config = getCurrentConfig();
    if (!config) return;

    const currentHandle = getCurrentProductHandle();
    if (!currentHandle) return;

    const sessionKey = 'allora-related-popup-shown-' + currentHandle;

    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, '1');

    setTimeout(function () {
      openPopup(config);
    }, 1400);
  });
})();


/* =========================
   Allora UPS Shipping Question Popup
   Intercepts Add to Cart for tagged B2B customers
   on specific product pages, after a timed delay.
   Uses an invisible overlay to intercept SparkLayer's
   shadow DOM add-to-cart button.
   ========================= */

(function () {

  /* ----------------------------------------
     CONFIGURATION — edit these values only
     ---------------------------------------- */

  const UPS_CONFIG = {
    // Keys are product page handles, values are the bulk page URL for that product.
    // Add or remove entries as needed.
        targetHandles: {
      'ada-vcs-1218-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-18-x-13-undermount-porcelain-ada-rectangular-bathroom-sink-ada-vcs-1116-r',
      'ada-vcs-1116-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-20-ada-white-porcelain-undermount-ada-rectangular-bathroom-sink-ada-vcs-1218-r',
      'ada-vcs-1318-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-21-x-15-ada-white-porcelain-undermount-rectangular-bathroom-sink-ada-vcs-1318-r',
      'ada-vcs-1417-o-white-oval-porcelain-undermount-sink': '/products/bulk-packed-20-x-16-ada-depth-white-oval-porcelain-undermount-bathroom-sink-ada-vcs-1417-o',
      'vcs-1417-o-white-oval-porcelain-undermount-sink': '/products/bulk-packed-19-1-4-oval-porcelain-undermount-bathroom-sink-in-white-vcs-1417-o',
      'vcs-1316-o-white-oval-porcelain-undermount-sink': '/products/bulk-packed-18-1-2-oval-porcelain-undermount-bathroom-sink-in-white-vcs-1316-o',
      'vcs-1215-o-white-oval-porcelain-undermount-sink': '/products/bulk-17-1-8-oval-porcelain-undermount-bathroom-sink-with-overflow-drain-in-white-vcs-1215-o',
      'vcs-1114-o-white-oval-porcelain-undermount-sink': '/products/bulk-packed-16-1-4-oval-porcelain-undermount-bathroom-sink-in-white-vcs-1114-o',
      'vcs-1013-o-white-oval-porcelain-undermount-sink': '/products/bulk-packed-15-oval-porcelain-undermount-bathroom-sink-in-white-vcs-1013-o',
      'vcs-1317-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-20-x-16-rectangular-porcelain-undermount-bathroom-sink-in-white-vcs-1317-r',
      '18-rectangular-porcelain-undermount-bathroom-sink-with-overflow-drain-in-white-vcs-1016-r': '/products/bulk-packed-18-x-12-rectangular-porcelain-undermount-bathroom-sink-with-overflow-in-white-vcs-1016-r-copy',
      'vcs-1115-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-17-x-13-rectangular-porcelain-undermount-bathroom-sink-in-white-vcs-1115-r-copy',
      'vcs-1318-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-20-1-2-rectangular-porcelain-undermount-bathroom-sink-in-white-vcs-1318-r',
      'vcs-1218-n-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-20-5-8-white-rectangular-vitreous-china-undermount-bathroom-sink-vcs-1218-n-r',
      'vcs-1116-r-rectangle-porcelain-undermount-sink': '/products/18-1-4-rectangular-porcelain-undermount-bathroom-sink-with-overflow-drain-in-white-vcs-1116-r-copy',
      'ada-vcs-2017-o-t-white-oval-porcelain-top-mount-sink': '/products/bulk-packed-20-1-2-ada-depth-oval-porcelain-top-mount-bathroom-sink-in-white-ada-vcs-2017-o-t-copy',
      'vcs-1216-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-18-1-8-rectangular-porcelain-undermount-bathroom-sink-in-white-vcs-1216-r',
      'vcs-1218-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-20-5-8-rectangular-porcelain-undermount-bathroom-sink-in-white-vcs-1218-r',
      'vcs-1421-r-rectangle-porcelain-undermount-sink': '/products/bulk-packed-23-3-4-rectangular-porcelain-undermount-bathroom-sink-in-white-vcs-1421-r',
      '16-x-16-white-semi-recessed-porcelain-vessel-sink-with-overflow-vcs-1616-r-t': '/products/16-x-16-white-semi-recessed-porcelain-vessel-sink-with-overflow-vcs-1616-r-t-copy'
    },

    // Tags that disqualify a customer from seeing this popup.
    // Any customer with one of these tags will be skipped.
    // Customers with NO tags at all are also skipped.
    excludedTags: ['b2b-b2b20', 'b2b-b2b35'],

    // How long (ms) after page load before the timed popup appears unprompted.
    // Set to null to disable the timed trigger and rely only on ATC interception.
    timedDelayMs: 8000,

    // Session storage key — prevents re-showing once the customer has answered.
    sessionKey: 'allora-ups-popup-answered'
  };

  /* ----------------------------------------
     INTERNALS — no edits needed below here
     ---------------------------------------- */

  const POPUP_ID   = 'allora-ups-popup';
  const STYLE_ID   = 'allora-ups-popup-styles';
  const OVERLAY_ID = 'allora-spark-overlay';

  // ── Customer eligibility ────────────────────────────────────────────────────

  function customerIsEligible() {
    let tags = [];

    // Primary: Liquid-injected window.alloraCustomer (set in theme.liquid)
    if (window.alloraCustomer && Array.isArray(window.alloraCustomer.tags)) {
      tags = window.alloraCustomer.tags;
    }
    // Fallback: SparkLayer customer object
    else if (window.SparkLayer && window.SparkLayer.customer) {
      tags = window.SparkLayer.customer.tags || [];
    }

    const normalized = tags.map(function (t) { return t.toLowerCase().trim(); });

    // Exclude: no tags at all (guest or untagged customer)
    if (normalized.length === 0) return false;

    // Exclude: has any disqualifying tag
    const excluded = UPS_CONFIG.excludedTags.map(function (t) { return t.toLowerCase().trim(); });
    if (excluded.some(function (t) { return normalized.includes(t); })) return false;

    return true;
  }

  // ── Page matching ───────────────────────────────────────────────────────────

  function getCurrentHandle() {
    const match = window.location.pathname.match(/^\/products\/([^/?#]+)/i);
    return match ? match[1] : null;
  }

  function isTargetPage() {
    const handle = getCurrentHandle();
    if (!handle) return false;
    return handle in UPS_CONFIG.targetHandles;
  }

  function getBulkPageUrl() {
    return UPS_CONFIG.targetHandles[getCurrentHandle()] || '/';
  }

  // ── Session state ───────────────────────────────────────────────────────────

  function alreadyAnswered() {
    return !!sessionStorage.getItem(UPS_CONFIG.sessionKey);
  }

  function markAnswered() {
    sessionStorage.setItem(UPS_CONFIG.sessionKey, '1');
  }

  // ── SparkLayer ATC button helper ────────────────────────────────────────────

  function getSparkAtcButton() {
    const sparkPdp = document.querySelector('spark-pdp');
    if (!sparkPdp || !sparkPdp.shadowRoot) return null;
    return sparkPdp.shadowRoot.querySelector('button.btn-small');
  }

  // ── Styles ──────────────────────────────────────────────────────────────────

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .ups-popup-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.50);
        z-index: 10000;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }
      .ups-popup-overlay.is-open {
        display: flex;
      }
      .ups-popup {
        width: 100%;
        max-width: 480px;
        background: #ffffff;
        border-radius: 4px;
        box-shadow: 0 20px 50px rgba(0,0,0,0.20);
        padding: 36px 32px 32px;
        position: relative;
        text-align: center;
      }
      .ups-popup-icon {
        font-size: 40px;
        margin-bottom: 14px;
      }
      .ups-popup-title {
        font-size: 20px;
        font-weight: 700;
        color: #111111;
        margin: 0 0 12px;
        line-height: 1.2;
      }
      .ups-popup-body {
        font-size: 15px;
        color: #444444;
        margin: 0 0 28px;
        line-height: 1.5;
      }
      .ups-popup-actions {
        display: flex;
        gap: 12px;
        justify-content: center;
        flex-wrap: wrap;
      }
      .ups-popup-btn {
        appearance: none;
        border: 0;
        font-size: 15px;
        font-weight: 600;
        padding: 14px 32px;
        border-radius: 4px;
        cursor: pointer;
        min-width: 130px;
        transition: opacity 0.15s ease;
      }
      .ups-popup-btn:hover { opacity: 0.88; }
      .ups-popup-btn--yes {
        background: #2f96db;
        color: #ffffff;
      }
      .ups-popup-btn--no {
        background: #f3f4f6;
        color: #333333;
        border: 1px solid #d1d5db;
      }
      .ups-popup-close {
        appearance: none;
        border: 0;
        background: none;
        position: absolute;
        top: 10px;
        right: 12px;
        width: 32px;
        height: 32px;
        cursor: pointer;
        font-size: 24px;
        line-height: 1;
        color: #9ca3af;
      }
      .ups-popup-note {
        font-size: 12px;
        color: #9ca3af;
        margin-top: 18px;
      }
    `;
    document.head.appendChild(style);
  }

  // ── Markup ──────────────────────────────────────────────────────────────────

  function ensureMarkup() {
    if (document.getElementById(POPUP_ID)) return;

    const overlay = document.createElement('div');
    overlay.id = POPUP_ID;
    overlay.className = 'ups-popup-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'ups-popup-title');
    overlay.innerHTML = `
      <div class="ups-popup">
        <button class="ups-popup-close" type="button" aria-label="Close">&#x2715;</button>
        <div class="ups-popup-icon">📦</div>
        <h2 id="ups-popup-title" class="ups-popup-title">Shipping Method</h2>
        <p class="ups-popup-body">
          Will you be shipping this item using UPS?
        </p>
        <div class="ups-popup-actions">
          <button class="ups-popup-btn ups-popup-btn--yes" type="button">Yes, ship via UPS</button>
          <button class="ups-popup-btn ups-popup-btn--no"  type="button">No, use bulk order</button>
        </div>
        <p class="ups-popup-note">Selecting "No" will take you to our bulk ordering page.</p>
      </div>
    `;

    document.body.appendChild(overlay);

    // Close on overlay background or X button click
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.classList.contains('ups-popup-close')) {
        closeUpsPopup();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeUpsPopup();
    });
  }

  // ── Open / close ────────────────────────────────────────────────────────────

  // _pendingSubmitFn is set when the popup was triggered by an ATC interception.
  // If Yes, we call it. If triggered by the timed trigger, it stays null.
  var _pendingSubmitFn = null;

  function openUpsPopup(pendingSubmitFn) {
    ensureStyles();
    ensureMarkup();

    _pendingSubmitFn = pendingSubmitFn || null;

    const overlay = document.getElementById(POPUP_ID);
    if (!overlay) return;

    // Clone buttons to remove any stale listeners from previous opens
    const yesBtn = overlay.querySelector('.ups-popup-btn--yes');
    const noBtn  = overlay.querySelector('.ups-popup-btn--no');
    yesBtn.replaceWith(yesBtn.cloneNode(true));
    noBtn.replaceWith(noBtn.cloneNode(true));

    overlay.querySelector('.ups-popup-btn--yes').addEventListener('click', function () {
      markAnswered();
      closeUpsPopup();
      if (_pendingSubmitFn) {
        _pendingSubmitFn();
        _pendingSubmitFn = null;
      }
    });

    overlay.querySelector('.ups-popup-btn--no').addEventListener('click', function () {
      markAnswered();
      closeUpsPopup();
      window.location.href = getBulkPageUrl();
    });

    overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeUpsPopup() {
    const overlay = document.getElementById(POPUP_ID);
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.style.overflow = '';
  }

  // ── SparkLayer shadow DOM overlay interception ──────────────────────────────

  function positionOverlay(overlayEl, targetEl) {
    const rect = targetEl.getBoundingClientRect();
    overlayEl.style.position = 'fixed';
    overlayEl.style.top      = rect.top + 'px';
    overlayEl.style.left     = rect.left + 'px';
    overlayEl.style.width    = rect.width + 'px';
    overlayEl.style.height   = rect.height + 'px';
    overlayEl.style.zIndex   = '9998';
    overlayEl.style.cursor   = 'pointer';
    overlayEl.style.background = 'transparent';
  }

  function injectSparkOverlay() {
    if (!isTargetPage() || !customerIsEligible() || alreadyAnswered()) return;

    const sparkPdp = document.querySelector('spark-pdp');
    if (!sparkPdp) return;

    // Don't double-inject
    if (document.getElementById(OVERLAY_ID)) return;

    const overlayEl = document.createElement('div');
    overlayEl.id = OVERLAY_ID;
    document.body.appendChild(overlayEl);
    positionOverlay(overlayEl, sparkPdp);

    // Keep overlay aligned on scroll and resize
    window.addEventListener('scroll', function () { positionOverlay(overlayEl, sparkPdp); }, { passive: true });
    window.addEventListener('resize', function () { positionOverlay(overlayEl, sparkPdp); }, { passive: true });

    overlayEl.addEventListener('click', function () {
      // If already answered yes this session, just pass the click through
      if (alreadyAnswered()) {
        overlayEl.remove();
        const atcBtn = getSparkAtcButton();
        if (atcBtn) atcBtn.click();
        return;
      }

      openUpsPopup(function () {
        // Yes was clicked — remove our overlay and trigger the real ATC button
        overlayEl.remove();
        const atcBtn = getSparkAtcButton();
        if (atcBtn) atcBtn.click();
      });
    });

    // If SparkLayer re-renders spark-pdp, reposition or re-inject
    const observer = new MutationObserver(function () {
      const stillExists = document.getElementById(OVERLAY_ID);
      if (!stillExists) {
        injectSparkOverlay();
      } else {
        positionOverlay(overlayEl, sparkPdp);
      }
    });
    observer.observe(sparkPdp, { childList: true, subtree: true });
  }

  // SparkLayer may not have rendered spark-pdp immediately — retry until found
  function waitForSparkAndInject() {
    if (document.querySelector('spark-pdp')) {
      injectSparkOverlay();
    } else {
      const observer = new MutationObserver(function () {
        if (document.querySelector('spark-pdp')) {
          observer.disconnect();
          injectSparkOverlay();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ── Standard (non-SparkLayer) ATC form interception ────────────────────────

  function interceptAddToCart() {
    document.querySelectorAll('form[action="/cart/add"]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        if (!isTargetPage()) return;
        if (!customerIsEligible()) return;
        if (alreadyAnswered()) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        openUpsPopup(function () {
          form.submit();
        });
      }, true); // capture phase to beat other listeners
    });
  }

  // ── Timed trigger ───────────────────────────────────────────────────────────

  function initTimedTrigger() {
    if (!UPS_CONFIG.timedDelayMs) return;

    setTimeout(function () {
      if (!isTargetPage()) return;
      if (!customerIsEligible()) return;
      if (alreadyAnswered()) return;

      openUpsPopup(null); // informational only — no pending ATC action
    }, UPS_CONFIG.timedDelayMs);
  }

  // ── Init ────────────────────────────────────────────────────────────────────

  function init() {
    if (!isTargetPage()) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        interceptAddToCart();
        initTimedTrigger();
        waitForSparkAndInject();
      });
    } else {
      interceptAddToCart();
      initTimedTrigger();
      waitForSparkAndInject();
    }
  }

  init();

})();